import _ from 'lodash';

import {DataFilter} from '../../../../../ducks/data-filter';
import {
    getForeignKeyEntityDataType,
    getPathDetails,
    unwrapNullableDataType,
} from '../../../../../ducks/formattersettingshelpers';

import {renderTemplateString} from '../../FormatStringFormatter';

/**
 * Checks whether the given column can have a quick filter applied.
 *
 * @param {object} column
 * @param {object} formatter
 * @param {object} entityType
 * @returns {boolean} Indicates whether the column can have a quick filter applied.
 */
export const canUseQuickFilter = (column, formatter, entityType) => {
    const {
        finalAttribute: {dataType},
    } = getPathDetails(entityType, column.attributePath);

    if (dataTypeCanSTRCON(dataType)) {
        return true;
    }

    if (formatter.settings?.quickFilterTemplateString) {
        return true;
    }

    const referencedDataType = getForeignKeyEntityDataType(dataType);
    if (referencedDataType && _.isFunction(formatter.projectionFunction)) {
        const projections = formatter.projectionFunction(formatter.settings);

        // no projection string was specified in the configuration,
        // we can't infer what attributes are used and therefore
        // we can't apply quick filters for this attribute
        if (!projections) return false;

        // if at least one of the projections includes a wildcard
        // we can't infer what all the attributes are and therefore
        // we can't apply quick filters for this attribute
        if (projections.some((x) => x.includes('*'))) return false;

        const projectedAttributeDetails = projections.map((x) => getPathDetails(referencedDataType, [x]));

        // this attribute can be filtered on, only if all the projected attributes
        // can be filtered on
        return projectedAttributeDetails.every((x) => {
            return x.validPath && dataTypeCanSTRCON(x.finalAttribute.dataType);
        });
    }

    return false;
};

/**
 * Splits the given string into terms by whitespace. Preserves quoted terms.
 * For example, `foo bar "baz qux"` would be split into `['foo', 'bar', 'baz qux']`.
 *
 * @param {string} query The string to split.
 * @returns {Array<string>} The terms. Omits empty strings.
 */
const splitIntoTerms = (query) => {
    const terms = [];

    let currentTerm = '';
    let isQuoted = false;
    for (let i = 0; i < query.length; i++) {
        const char = query[i];
        if (char === ' ' && !isQuoted) {
            if (currentTerm) {
                terms.push(currentTerm);
                currentTerm = '';
            }
        } else if (char === '"') {
            isQuoted = !isQuoted;
        } else {
            currentTerm += char;
        }
    }

    if (currentTerm) {
        terms.push(currentTerm);
    }

    return terms.map(_.trim).filter(_.negate(_.isEmpty));
};

/**
 * Builds the quick filter string for the given column.
 *
 * **Important:** this function assumes the column can have a quick filter applied, use `canUseQuickFilter` to check.
 *
 * @param {string} query
 * @param {object} column
 * @param {object} formatter
 * @param {object} entityType
 *
 * @returns {string | null} The quick filter string or `null` if it can't be constructed.
 */
export const buildQuickFilter = (query, column, formatter, entityType) => {
    const {
        finalAttribute: {dataType},
        pathName,
    } = getPathDetails(entityType, column.attributePath);

    if (formatter.settings?.quickFilterTemplateString) {
        try {
            return renderTemplateString(formatter.settings.quickFilterTemplateString, {
                attribute: pathName,
                query,
            });
        } catch (e) {
            console.error(e);
            return null;
        }
    }

    if (dataTypeCanSTRCON(dataType)) {
        return DataFilter.contains(pathName, query);
    }

    const referencedDataType = getForeignKeyEntityDataType(dataType);
    if (referencedDataType) {
        // note: assumes that there are valid projections, since we assume the column was validated for quick filters
        const projections = formatter.projectionFunction(formatter.settings);
        const projectedAttributeDetails = projections.map((projection) =>
            getPathDetails(referencedDataType, [projection]),
        );

        // split the query into terms, since the format string formatter might be displaying
        // multiple attributes as a single string without the user having to be aware of that
        // in that case we want to look for each term in each of the projected attributes
        // for example, attribute "address" has sub-attributes "street" and "number" and the formatter
        // shows them as "<street> <number>", the user should be able to search for "road 20" and still get results
        // therefore we'll look for each term in each attribute, eg:
        // street -> should contain "road" or "20"
        // number -> should contain "road" or "20"
        const terms = splitIntoTerms(query);

        return DataFilter.or(
            terms.flatMap((term) =>
                projectedAttributeDetails
                    .map((x) => {
                        const {dataType} = x.finalAttribute;
                        if (dataTypeCanSTRCON(dataType)) {
                            // prepend the projected attribute path with the original column attribute path
                            // since the projected attribute is a nested FK attribute
                            return DataFilter.contains(`${pathName}.${x.pathName}`, term);
                        }
                    })
                    .filter(_.isString),
            ),
        );
    }

    return null;
};

const dataTypeCanSTRCON = (dataType) => {
    const unwrapped = unwrapNullableDataType(dataType);

    return (
        unwrapped.discriminatorValue === 'PrimitiveDataType' && ['String', 'Int', 'Decimal'].includes(unwrapped.type)
    );
};
